import { call, put, select, takeLatest } from "redux-saga/effects";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { AnyEntity, EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import { MapEntry } from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel, {
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import localSettings from "@app/config/LocalSettings";
import { getDatesRangeAtOffset } from "@app/enum/dashboard/dateRanges";
import ViewMode from "@app/enum/dashboard/ViewMode";
import DrillDownViewState from "@app/scene/dashboard/types/DrillDownViewState";
import { ApiMethodName } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { exportEntities } from "@app/store/exportEntities/actions";
import { getOrganizationId, getOrganizationSize } from "@app/store/iam";
import { getMapViewSettings } from "@app/store/map";
import EntityTypesSupportedInSkippedRecords from "@app/types/dashboard/cards/skippedRecords/EntityTypesSupportedInSkippedRecords";
import SkippedRecordsCardConfiguration from "@app/types/dashboard/cards/skippedRecords/SkippedRecordsCardConfiguration";
import { allSettled, SettleResult } from "@app/util/effects";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import FieldModel from "@app/util/fieldModel/impl/FieldModel";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import {
  createAndCondition,
  createEmptyFilterModel,
  createSimpleCondition,
} from "@app/util/filters/utils";
import loggingService from "@app/util/logging";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { toLeanPinsRequest } from "@app/util/map/leanPinsRequest";
import { takeLatestBy } from "@app/util/redux/takeLatestBy";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import getDefaultDrillDownColumns from "../../utils/getDefaultDrillDownColumns";

import {
  applySkippedRecordsDrillDownListViewSettings,
  exportSkippedRecordsCardDrillDownData,
  fetchSkippedRecordsCardData,
  fetchSkippedRecordsCardDrillDownData,
  showSkippedRecordsDrillDown,
} from "./actions";
import buildSkippedRecordsPinsFilters, {
  buildSkippedRecordsConditions,
} from "./buildSkippedRecordsPinsFilters";
import { fetchDayTripPaths } from "./fetchDayTripPaths";
import {
  getSkippedRecordsDrillDownConfiguration,
  getSkippedRecordsDrillDownSpansBack,
  getSkippedRecordsDrillDownTotalFilteredRecords,
  getSkippedRecordsDrillDownViewStates,
} from "./selectors";

// The backend requires tripInteractions in $columns or it omits the field from _source.
const PINS_COLUMNS: string[] = ["tripInteractions", "cadenceStatus", "geoPoint", "id"];

const LIST_PAGE_SIZE = 20;

// Default drill-down columns per entity type (Task 16 defaults; persisted via localSettings).
const defaultColumns: Record<EntityTypesSupportedInSkippedRecords, string[]> = {
  [EntityType.COMPANY]: [
    CompanyFieldName.NAME,
    CompanyFieldName.USER,
    CompanyFieldName.ADDRESS,
    CompanyFieldName.LAST_ACTIVITY_DATE,
    CompanyFieldName.FREQUENCY_INTERVAL,
    CompanyFieldName.FREQUENCY_STATUS,
  ],
  [EntityType.DEAL]: [
    DealFieldName.NAME,
    DealFieldName.USER,
    DealFieldName.ADDRESS,
    DealFieldName.LAST_ACTIVITY_DATE,
    DealFieldName.FREQUENCY_INTERVAL,
    DealFieldName.FREQUENCY_STATUS,
  ],
  [EntityType.PERSON]: [
    PersonFieldName.NAME,
    PersonFieldName.USER,
    PersonFieldName.ADDRESS,
    PersonFieldName.LAST_ACTIVITY_DATE,
    PersonFieldName.FREQUENCY_INTERVAL,
    PersonFieldName.FREQUENCY_STATUS,
  ],
};

const defaultSortColumn: Record<EntityTypesSupportedInSkippedRecords, string> = {
  [EntityType.COMPANY]: CompanyFieldName.LAST_ACTIVITY_DATE,
  [EntityType.DEAL]: DealFieldName.LAST_ACTIVITY_DATE,
  [EntityType.PERSON]: PersonFieldName.LAST_ACTIVITY_DATE,
};

const fetchMethodByEntityType: Record<EntityTypesSupportedInSkippedRecords, ApiMethodName> = {
  [EntityType.COMPANY]: "fetchCompanies",
  [EntityType.DEAL]: "fetchDeals",
  [EntityType.PERSON]: "fetchPeople",
};

function* onFetchSkippedRecordsCardData({
  payload,
}: ReturnType<typeof fetchSkippedRecordsCardData>) {
  const { callback, configuration, failureCallback, offset, scope, spansBack, viewMode, viewport } =
    payload;
  const { dateRange } = configuration;

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const mapViewState: MapViewState = yield select(getMapViewSettings);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);

    // How many whole spans the user has stepped back on the card face. Transient by design, so
    // it arrives on the action rather than the saved configuration.
    const datesRange = getDatesRangeAtOffset(dateRange.range, dateRange.subRange, spansBack ?? 0);
    const $filters = buildSkippedRecordsPinsFilters(configuration, datesRange, viewport?.bounds);

    // Augment per-entity filters with the standard include flags required by all pin fetches.
    const entityType = configuration.criteria.entityType;
    const augmentedEntities: PlatformFilterModel["entities"] = {
      [entityType]: {
        ...($filters.entities?.[entityType] ?? {}),
        cadence: true,
        includeCustomFields: true,
        includeGroups: true,
        includeNotes: true,
        includeRoutes: true,
        includeTerritories: true,
      },
    };

    // Limit results to the dashboard scope: a team (team dashboard) or a single rep (user
    // dashboard). Omitted for the org-wide dashboard. Mirrors the Efficiency Mini-Map.
    const scopeConditions = [
      ...(!scope?.userId && scope?.teamId ? [{ teamId: { $in: [scope.teamId] } }] : []),
      ...(scope?.userId ? [{ userId: { $in: [scope.userId] } }] : []),
    ];
    const scopeFilter = scopeConditions.length > 0 ? { $and: scopeConditions } : {};

    if (viewMode === ViewMode.MAP) {
      const response: ListResponse<MapEntry> = yield callApi(
        "fetchMapPins",
        orgId,
        toLeanPinsRequest(
          {
            ...$filters,
            ...scopeFilter,
            bounds: viewport?.bounds,
            entities: augmentedEntities,
            pinLegends: {
              [entityType]: {
                color: mapViewState.colorKey?.[entityType],
                shape: mapViewState.shapeKey?.[entityType],
              },
            } as PlatformFilterModel["pinLegends"],
            ...getPrecisionDetails(viewport?.zoom ?? 1, organizationSize),
          },
          { columns: ["name"], withoutLegends: true }
        )
      );

      // Trip routes are delivered with the LIST fetch (once) and read from there by the map, so
      // panning/zooming never re-fetches them.
      yield call(callback, {
        categorizedMapEntries: categorizeMapEntries(response.data),
        total: response.total,
      });
    } else {
      const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, {
        $columns: PINS_COLUMNS,
        $filters: {
          ...$filters,
          ...scopeFilter,
          entities: augmentedEntities,
        },
        $limit: LIST_PAGE_SIZE,
        $offset: offset,
      });

      if (offset) {
        // Subsequent pages: append the rows.
        yield call(callback, { entities: response.data, total: response.total });
      } else {
        // First page: fetch the day's trip routes once (best-effort) and deliver them together in
        // a single callback, so a fast infinite-scroll append can't be clobbered by a late paths
        // callback.
        const [travelPathsResult]: [SettleResult<google.maps.LatLngLiteral[][]>] = yield allSettled(
          [call(fetchDayTripPaths, dateRange, scope, spansBack ?? 0)]
        );
        yield call(callback, {
          entities: response.data,
          total: response.total,
          travelPaths: travelPathsResult.error ? [] : travelPathsResult.result,
        });
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

function* onOpenDrillDown({ payload }: ReturnType<typeof showSkippedRecordsDrillDown.request>) {
  const { configuration, scope } = payload;
  const { criteria } = configuration;

  const viewStates: Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState> = yield select(
    getSkippedRecordsDrillDownViewStates
  );

  const entityType = criteria.entityType;
  const fieldModel = getFieldModelByEntityType(entityType);

  const viewState = localSettings.getViewSettings(
    `dashboard/skippedRecords/${entityType}`,
    fieldModel,
    {
      ...viewStates[entityType],
      columns: getDefaultDrillDownColumns(defaultColumns[entityType], fieldModel),
      sort: [
        {
          field: fieldModel.getByName(defaultSortColumn[entityType])!,
          order: SortOrder.DESC,
        },
      ],
    }
  );

  // Skipped-records membership (skipped trip interaction within the day range) is applied as raw
  // tripInteractions conditions at fetch time — see onFetchDrillDownData. It cannot be expressed
  // through the field-model filter, so the stored filter only carries scope (team/user) conditions.
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const scopeConditions = [
    ...(isTeamScope
      ? [createSimpleCondition(CommonFieldName.TEAM, FilterOperator.IN_ANY, [scope.teamId!])]
      : []),
    ...(scope?.userId
      ? [createSimpleCondition(CommonFieldName.USER, FilterOperator.IN_ANY, [scope.userId])]
      : []),
  ];

  yield put(
    showSkippedRecordsDrillDown.success({
      viewStates: {
        ...viewStates,
        [entityType]: { ...viewState, filter: createAndCondition(scopeConditions) },
      },
    })
  );
}

export function* onFetchDrillDownData({
  payload,
}: ReturnType<typeof fetchSkippedRecordsCardDrillDownData.request>) {
  try {
    loggingService.debug("Dashboard: skipped records card, onFetchDrillDownData", payload);
    // Clone the request before stripping the AgGrid filter so we never mutate the action payload.
    const request = { ...payload.request };
    if (!payload.updateOnly) {
      // We do not listen to filter returned by AgGrid from PlatformDataSource
      delete request.filter;
    }

    if (!payload.fetchOnlyWithoutFilters) {
      yield put(
        applySkippedRecordsDrillDownListViewSettings({
          ...request,
          entityType: payload.entityType,
        })
      );
    }

    const drillDownViewStates: Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState> =
      yield select(getSkippedRecordsDrillDownViewStates);
    const drillDownViewState = drillDownViewStates[payload.entityType];

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(
        drillDownViewState,
        `dashboard/skippedRecords/${payload.entityType}`
      );
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && request.range
        ? request.range.startRow
        : drillDownViewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && request.range
        ? request.range.endRow - request.range.startRow
        : drillDownViewState.range.endRow - drillDownViewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const fieldModel: FieldModel = yield call(getFieldModelByEntityType, payload.entityType);

    // Scope the drill-down to skipped records exactly like the map/list, via the shared condition
    // builder: trip within the day range + "did not log" the selected activity types (or no
    // activity at all) + selected sources. These raw nested-field conditions can't be expressed
    // through the field-model filter, so they're injected here on every fetch.
    const configuration: SkippedRecordsCardConfiguration | undefined = yield select(
      getSkippedRecordsDrillDownConfiguration
    );
    // View Data has to show the span the card face is showing, not the saved default, so the
    // navigated offset is read back from the store alongside the configuration.
    const spansBack: number | undefined = yield select(getSkippedRecordsDrillDownSpansBack);
    const tripInteractionConditions: PlatformFilterCondition[] = configuration
      ? buildSkippedRecordsConditions(
          configuration.criteria,
          getDatesRangeAtOffset(
            configuration.dateRange.range,
            configuration.dateRange.subRange,
            spansBack ?? 0
          )
        )
      : [];

    const platformFilters = convertToPlatformFilterModel(
      payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : drillDownViewState.filter,
      drillDownViewState.columns,
      fieldModel
    );

    const requestPayload = {
      $filters: {
        includeAccessStatus: true,
        ...platformFilters,
        $and: [...(platformFilters.$and ?? []), ...tripInteractionConditions],
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(drillDownViewState.sort),
    };

    const fetchMethod = fetchMethodByEntityType[payload.entityType];
    const response: ListResponse<AnyEntity> = yield callApi(fetchMethod, orgId, requestPayload);
    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }
    yield put(
      fetchSkippedRecordsCardDrillDownData.success({
        entityType: payload.entityType,
        totalFilteredRecords: response.total,
        totalRecords: response.accessible,
      })
    );
  } catch (error) {
    payload.failCallback?.();
    yield put(fetchSkippedRecordsCardDrillDownData.failure(error));
    yield put(handleError({ error }));
  }
}

function* onExport({ payload }: ReturnType<typeof exportSkippedRecordsCardDrillDownData>) {
  const drillDownViewStates: Record<EntityTypesSupportedInSkippedRecords, DrillDownViewState> =
    yield select(getSkippedRecordsDrillDownViewStates);
  const total: Record<EntityTypesSupportedInSkippedRecords, number> = yield select(
    getSkippedRecordsDrillDownTotalFilteredRecords
  );

  yield put(
    exportEntities.request({
      entityType: payload.entityType,
      total: total[payload.entityType],
      viewState: drillDownViewStates[payload.entityType],
    })
  );
}

export function* skippedRecordsSagas() {
  // Several cards of this type can share a board, so we key by card id rather than using a
  // plain takeLatest. The list and map views are fetched in parallel for the same card, so the
  // view mode is part of the key — otherwise the map fetch would cancel the in-flight list
  // fetch (or vice versa) and one of them would never resolve.
  yield takeLatestBy(
    fetchSkippedRecordsCardData,
    onFetchSkippedRecordsCardData,
    (action: ReturnType<typeof fetchSkippedRecordsCardData>) =>
      `${action.payload.cardId}:${action.payload.viewMode}`
  );
  yield takeLatest(showSkippedRecordsDrillDown.request, onOpenDrillDown);
  yield takeLatest(fetchSkippedRecordsCardDrillDownData.request, onFetchDrillDownData);
  yield takeLatest(exportSkippedRecordsCardDrillDownData, onExport);
}
